import React, { FC, useMemo, useState } from 'react'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { Txt, LineLimitTxt, TableCell, Icon, Tooltip } from 'src/components/atoms'
import { UserRoleTxt, DateTxt, ProjectCountTxt } from 'src/components/molecules'
import { DeleteUserModal } from 'src/components/organisms'

import { useAuthority, useGetUsers, useToast } from 'src/hooks'
import { SelectableInternalUserEntity } from 'src/types'

type Props = {
  user: SelectableInternalUserEntity
  onSelectUser: (user: SelectableInternalUserEntity) => void
  className?: string
}

export const InternalUserListTableRow: FC<Props> = ({ user, onSelectUser, className }) => {
  const [deleteUser, setDeleteUser] = useState<SelectableInternalUserEntity | null>(null)

  const { displayStandardTextToast } = useToast()
  const { users, refetch } = useGetUsers()
  const { canAssignProject, canDeleteUser, isSignInUser } = useAuthority()

  const handleClickDeleteUser = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    setDeleteUser(user)
  }

  const handleOnSelect = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onSelectUser(user)
  }

  const afterDelete = async () => {
    displayStandardTextToast('ユーザーを削除しました')
    await refetch()
  }

  const isLastSuperAdminUser = useMemo(() => {
    if (!users || user.role.name !== 'superAdmin') {
      return false
    }
    return !users.some(u => u.id !== user.id && u.role.name === 'superAdmin')
  }, [user.id, user.role.name, users])

  return (
    <div className={className}>
      <div className="tw-flex tw-flex-nowrap tw-items-center tw-accent-standardText-secondary tw-h-[58px] tw-bg-surface-primary hover:tw-bg-surface-secondary">
        {canAssignProject && (
          <TableCell width={5}>
            <div onClick={handleOnSelect} role="presentation">
              <Checkbox nonce={undefined} checked={user.isSelected} padding="--s0" />
            </div>
          </TableCell>
        )}
        <TableCell width={6} align="right" className="tw-mr-s1">
          <Txt sizeType="s" className="tw-text-standardText-secondary">
            {user.id}
          </Txt>
        </TableCell>
        <TableCell width={15}>
          <LineLimitTxt
            maxLineLimit={2}
            className={`${
              isSignInUser({ userId: user.id })
                ? 'tw-text-pattern6-3'
                : 'tw-text-standardText-primary'
            } tw-font-bold tw-mr-s2`}
          >
            {user.name ?? '-'}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={14}>
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className="tw-text-standardText-primary tw-mr-s2"
          >
            {user.email}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={6} className="tw-mr-s1">
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className="tw-text-standardText-secondary tw-mr-s2"
          >
            {user.keywords.length > 0 ? user.keywords.join(', ') : '-'}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={6} className="tw-mr-s1">
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className="tw-text-standardText-secondary tw-mr-s2"
          >
            {user.customRoles.length > 0 ? user.customRoles.map(role => role.name).join(', ') : '-'}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={9} className="tw-mr-s1">
          <UserRoleTxt role={user.role} size="s" />
        </TableCell>
        <TableCell width={6} className="tw-mr-s1">
          <DateTxt date={user.createdAt} sizeType="s" className="tw-text-standardText-secondary" />
        </TableCell>
        <TableCell width={6} align="right" className="tw-mr-s1">
          <ProjectCountTxt count={user.projectCount} className="tw-text-standardText-secondary" />
        </TableCell>
        <TableCell width={10}>
          <Txt sizeType="s" className="tw-text-standardText-secondary tw-ml-s3">
            {user.isResetPassword ? '設定済み' : '仮パスワード'}
          </Txt>
        </TableCell>
        {canDeleteUser && (
          <TableCell align="right" className="tw-mr-s-1">
            <Tooltip
              text={isLastSuperAdminUser ? '全権管理者は必ず1人存在するようにして下さい' : '削除'}
              placement="top"
              showIcon={false}
              disabled={!canDeleteUser}
            >
              <div
                role="presentation"
                className="tw-p-r-3"
                onClick={event =>
                  !isSignInUser({ userId: user.id }) &&
                  !isLastSuperAdminUser &&
                  handleClickDeleteUser(event)
                }
              >
                <Icon
                  path="trash/soft/line"
                  size="--r1"
                  className={
                    isLastSuperAdminUser
                      ? 'tw-text-standardText-disable'
                      : 'tw-text-standardText-secondary hover:tw-text-standardText-primary'
                  }
                />
              </div>
            </Tooltip>
          </TableCell>
        )}
      </div>

      {deleteUser && (
        <DeleteUserModal
          users={[user]}
          isOpen={!!deleteUser}
          onClose={() => setDeleteUser(null)}
          afterDelete={afterDelete}
        />
      )}
    </div>
  )
}
